import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, CheckCircle2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { resetPassword } from '@/lib/api';
import { AuthLayout } from '@/components/AuthLayout';
import formStyles from '@/components/Form.module.css';
import { FormError, useFormError } from '@/components/ui/FormError';

const schema = z.object({
  password: z.string().min(12, 'At least 12 characters'),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: 'Passwords must match',
  path: ['confirmPassword'],
});
type FormValues = z.infer<typeof schema>;

interface Props { onNavigate: (path: string) => void }

export function ResetPasswordPage({ onNavigate }: Props) {
  const { t } = useTranslation('authFlows');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [done, setDone] = useState(false);
  const [serverError, setServerError] = useFormError();
  // Independent reveal per field so a user can show whichever one
  // they're typing — accurate entry matters most where "must match"
  // bites. Mirrors the eye toggle on the PWA LoginPage.
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setEmail(params.get('email') ?? '');
    setToken(params.get('token') ?? '');
  }, []);

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (values: FormValues) => {
    setServerError('');
    if (!email || !token) {
      setServerError(t('reset.invalidToken'));
      return;
    }
    try {
      await resetPassword(email, token, values.password);
      setDone(true);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : t('reset.resetFailed'));
    }
  };

  return (
    <AuthLayout
      title={t('reset.title')}
      subtitle={email ? t('reset.subtitleFor', { email }) : t('reset.subtitleDefault')}
      footer={<a href="/login" onClick={(e) => { e.preventDefault(); onNavigate('/login'); }}><ArrowLeft size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {t('reset.backToSignIn')}</a>}
    >
      {done ? (
        <div className={formStyles.formSuccess} style={{ display: 'flex', gap: 10 }}>
          <CheckCircle2 size={18} />
          <span>{t('reset.successDone')} <a href="/login" onClick={(e) => { e.preventDefault(); onNavigate('/login'); }}>{t('reset.successSignIn')}</a> {t('reset.successSuffix')}</span>
        </div>
      ) : (
        <form className={formStyles.form} onSubmit={handleSubmit(onSubmit)}>
          {serverError && <FormError announce={serverError.nonce}>{serverError.message}</FormError>}
          <div className={formStyles.field}>
            <label className={formStyles.label}><Lock size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> {t('reset.newPasswordLabel')}</label>
            <div style={{ position: 'relative' }}>
              <input
                className={formStyles.input}
                type={showNew ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('reset.newPasswordPlaceholder')}
                {...register('password')}
                style={{ paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowNew((v) => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--color-tx-m)', cursor: 'pointer', padding: 6 }}
                aria-label={showNew ? t('reset.hidePassword') : t('reset.showPassword')}
              >
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && <span className={formStyles.errorMsg}>{errors.password.message}</span>}
          </div>
          <div className={formStyles.field}>
            <label className={formStyles.label}>{t('reset.confirmLabel')}</label>
            <div style={{ position: 'relative' }}>
              <input
                className={formStyles.input}
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('reset.confirmPlaceholder')}
                {...register('confirmPassword')}
                style={{ paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--color-tx-m)', cursor: 'pointer', padding: 6 }}
                aria-label={showConfirm ? t('reset.hidePassword') : t('reset.showPassword')}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.confirmPassword && <span className={formStyles.errorMsg}>{errors.confirmPassword.message}</span>}
          </div>
          <button type="submit" className={formStyles.btnPrimary} disabled={isSubmitting}>
            {isSubmitting ? t('reset.submitting') : t('reset.submit')}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
